import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Search, Plus, Eye, Edit, Trash2, CheckCircle, X, Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const OrdersDocumentsPage = () => {
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', orderType: '', description: '', physician: '', priority: 'Routine'
  });

  useEffect(() => {
    fetchOrders();
    fetchPatients();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      if (response.data.success) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientId || !formData.orderType || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }
    const patient = patients.find(p => p.id === formData.patientId);
    try {
      await api.post('/orders', { ...formData, patientName: `${patient.firstName} ${patient.lastName}` });
      toast.success('Order created');
      fetchOrders();
      resetForm();
    } catch (error) {
      toast.error('Error creating order');
    }
  };

  const handleSign = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/sign`);
      toast.success('Order signed');
      fetchOrders();
    } catch (error) {
      toast.error('Error signing order');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error('Error deleting order');
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setFormData({ patientId: '', patientName: '', orderType: '', description: '', physician: '', priority: 'Routine' });
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.orderType?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || order.status?.toLowerCase().includes(filterStatus.toLowerCase());
    return matchesSearch && matchesFilter;
  });

  const pendingCount = orders.filter(o => o.status === 'Pending Signature').length;
  const signedCount = orders.filter(o => o.status === 'Signed').length;

  const orderTypes = ['Medication Order', 'Lab Order', 'Therapy Order', 'DME Order', 'Home Health Order', 'Referral', 'Recertification'];

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#f39c12]" />
                Orders & Documents
              </h1>
              <p className="text-gray-500 text-sm mt-1">Manage physician orders and clinical documents</p>
            </div>
            <Button onClick={() => setShowAddForm(true)} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
              <Plus className="w-4 h-4 mr-2" />New Order
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
            <div className="text-sm text-gray-500">Pending Signature</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-green-600">{signedCount}</div>
            <div className="text-sm text-gray-500">Signed</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-blue-600">{orders.length}</div>
            <div className="text-sm text-gray-500">Total Orders</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-2xl font-bold text-purple-600">0</div>
            <div className="text-sm text-gray-500">Expired</div>
          </div>
        </div>

        {showAddForm && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Create New Order</h2>
              <Button variant="ghost" onClick={resetForm}><X className="w-5 h-5" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Patient *</Label>
                <select value={formData.patientId} onChange={(e) => setFormData({...formData, patientId: e.target.value})} className="w-full mt-1 p-2 border rounded">
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                </select>
              </div>
              <div>
                <Label>Order Type *</Label>
                <select value={formData.orderType} onChange={(e) => setFormData({...formData, orderType: e.target.value})} className="w-full mt-1 p-2 border rounded">
                  <option value="">Select Type</option>
                  {orderTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Physician</Label>
                <Input value={formData.physician} onChange={(e) => setFormData({...formData, physician: e.target.value})} placeholder="Dr. Johnson" className="mt-1" />
              </div>
              <div>
                <Label>Priority</Label>
                <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full mt-1 p-2 border rounded">
                  <option>Routine</option><option>Urgent</option><option>STAT</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full mt-1 p-3 border rounded" rows={3} placeholder="Order details..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-[#f39c12] hover:bg-[#e67e22] text-white"><Save className="w-4 h-4 mr-2" />Create Order</Button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search orders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <select className="border rounded-lg px-4 py-2" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="signed">Signed</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Physician</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No orders found. Create your first order above.</td></tr>
              ) : filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium">{order.patientName}</td>
                  <td className="px-6 py-4">{order.orderType}</td>
                  <td className="px-6 py-4 max-w-xs truncate">{order.description}</td>
                  <td className="px-6 py-4">{order.physician || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${order.status === 'Signed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {order.status === 'Pending Signature' && (
                        <Button variant="ghost" size="sm" onClick={() => handleSign(order.id)} title="Sign Order">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(order.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OrdersDocumentsPage;
